import { EntityTypeMap } from 'types/entity';
import { messages } from '@redviking/argonaut-core-ui/src/i18n/i18n';
import { DeployParamKind } from '@redviking/argonaut-util/src/mes/deployParam.zod';
import { type ValidationResult } from 'types';
import type { DeploymentEntity } from './appletDeployment.entity';

export function validateDeploymentParams (deploymentEntity: DeploymentEntity): ValidationResult {
    const deployment = deploymentEntity.deployment;
    
    // verify all required deployment params are set
    for (const ldv of deployment.linkedDesignVersions) {
        const referencedDesignVersion = deploymentEntity.referencedDesignVersions[ldv.designVersion.id];
        if (!referencedDesignVersion) {
            return {
                status: 'error',
                errorMessage: `Applet Design Version with id "${ldv.designVersion.id}" was not found in deployment validation`,
            };
        }

        // the validation for all dep param types at least ensures that the deploy param ALWAYS exists in BOTH the deployment defaults and the design version
        // the design version deploy param might be null to indicate that it is using the deployment default
        for (const dp of referencedDesignVersion.deployParams) {
            const deploymentDefault = deployment.config.deployParamVals[dp.deployParamId];
            if (deploymentDefault === undefined) {
                return {
                    status: 'error',
                    errorMessage: `Deployment Parameter "${dp.deployParamId}" is not found in the config`,
                };
            }

            // perform different validations depending on the type of the deployment param
            if (dp.deployParamKind === DeployParamKind.localVarValue) {
                // no validation needed, these are always optional
            } else if (dp.deployParamKind === DeployParamKind.location) {
                if (!deploymentDefault.val) {
                    return {
                        status: 'error',
                        errorMessage: `Location Deployment Parameter value "${dp.deployParamId}" is required`,
                    };
                }
            } else if (dp.deployParamKind === DeployParamKind.asset) {
                // the param must be defined _somewhere_
                if (!deploymentDefault.val) {
                    return {
                        status: 'error',
                        errorMessage: `Asset Deployment Parameter value "${dp.deployParamId}" is required`,
                    };
                }
            } else if (dp.deployParamKind === DeployParamKind.sparkplugNodeCfg) {
                if (deploymentDefault.kind !== DeployParamKind.sparkplugNodeCfg) {
                    return {
                        status: 'error',
                        errorMessage: `Deployment Parameter "${dp.deployParamId}" must be a Sparkplug Node Configuration`,
                    };
                }
                for (const [ fieldKey, fieldVal ] of Object.entries(deploymentDefault.val)) {
                    // eslint-disable-next-line max-depth
                    if (!fieldVal) {
                        return {
                            status: 'error',
                            errorMessage: `Sparkplug Node Deployment Parameter "${dp.deployParamId}" field "${fieldKey}" cannot be blank`,
                        };
                    }
                }
            } else {
                const unknownDeploymentParam: never = dp;
                return {
                    status: 'error',
                    errorMessage: `Unknown deploy param: ${JSON.stringify(unknownDeploymentParam)}`,
                };
            }
        }
    }
    return {
        status: 'success',
    };
}

export function validateAppletDeployment (entityData: EntityTypeMap['appletDeployment']): ValidationResult {
    // ensure at least one design is linked
    if (entityData.deployment.linkedDesignVersions.length === 0) {
        return {
            status: 'error',
            errorMessage: 'You must pick at least one design to include in the deployment',
        };
    }

    // ensure at least one design is linked
    if (entityData.deployment.linkedDesignVersions.length === 0) {
        return {
            status: 'error',
            errorMessage: 'You must pick at least one design to include in the deployment',
        };
    }

    if (!entityData.deployment.name) {
        return {
            status: 'error',
            errorMessage: messages.applet.deployment.errors.noName,
        };
    }

    const deploymentParamsResult = validateDeploymentParams(entityData);
    if (deploymentParamsResult.status !== 'success') {
        return deploymentParamsResult;
    }

    if (entityData.deployment.config.deploymentSparkplugNode.usingSparkplug) {
        if (!entityData.deployment.config.deploymentSparkplugNode.mqttConnectionId) {
            return {
                status: 'error',
                errorMessage: 'Deployments using sparkplug must select a mqtt connection',
            };
        }
    }

    return {
        status: 'success',
    };
}

if (import.meta.webpackHot) {
    import.meta.webpackHot.accept('@redviking/argonaut-core-ui/src/routing');
}
